import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    if (result.success) {
      const role = result.user?.role || result.role;
      if (next) navigate(next);
      else navigate(role === 'admin' ? '/admin' : '/dashboard');
    } else setError(result.error);
    setLoading(false);
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center px-4 sm:px-6 lg:px-8 hero-gradient" data-testid="login-page">
      <div className="max-w-md w-full">
        <div className="card-surface p-8 sm:p-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-2 text-strong" data-testid="login-heading">Welcome Back</h2>
            <p className="text-sm text-muted" data-testid="login-description">Sign in to access your widgets</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-lg border border-red-500/30 bg-red-500/5 flex items-center space-x-2" data-testid="login-error-message">
              <AlertCircle className="w-5 h-5 text-red-400" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-default mb-2">Email</label>
                <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full input-field px-4 py-3 text-sm" data-testid="login-email-input" />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-default mb-2">Password</label>
                <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full input-field px-4 py-3 text-sm" data-testid="login-password-input" />
              </div>

              <button type="submit" disabled={loading}
                className="w-full btn-primary px-6 py-3.5 inline-flex items-center justify-center space-x-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="login-submit-button">
                <LogIn className="w-4 h-4" />
                <span>{loading ? 'Signing in...' : 'Sign In'}</span>
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-muted">
              Don't have an account?{' '}
              <Link to="/register" className="brand-green font-semibold hover:opacity-90" style={{ color: '#39FF14' }} data-testid="login-register-link">
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
